/**
 * Role-based route guards.
 *
 * - `RequireAuth`  — gates any authenticated area; redirects anon users to /login
 *   (preserving the attempted path). Shows a spinner while the session hydrates.
 * - `RequireRole`  — additionally restricts to a set of roles (META_ADMIN /
 *   ORG_ADMIN); a wrong-role user is redirected to their home area.
 *
 * These mirror the backend's two-tier model: meta-admin owns /meta/*, org-admin
 * owns /org/*. The server is still the real enforcement point — these guards are
 * UX (no sensitive data is exposed client-side; every API call is re-checked).
 */
import type { ReactElement, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { Role } from '@hotline/contracts';

import { useAuth } from './AuthContext.js';

/** The default landing route for a given role. */
export function homePathForRole(role: Role | undefined): string {
  return role === 'META_ADMIN' ? '/meta/orgs' : '/org/tickets';
}

export function RequireAuth({ children }: { children: ReactNode }): ReactElement {
  const { status, principal } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return <div className="route-loading" role="status">Loading…</div>;
  }
  if (status === 'anonymous' || !principal) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

export function RequireRole({
  roles,
  children,
}: {
  roles: Role[];
  children: ReactNode;
}): ReactElement {
  const { status, principal } = useAuth();

  if (status === 'loading') {
    return <div className="route-loading" role="status">Loading…</div>;
  }
  if (status === 'anonymous' || !principal) {
    return <Navigate to="/login" replace />;
  }
  if (!roles.includes(principal.role)) {
    // Wrong role — bounce to the caller's own home area.
    return <Navigate to={homePathForRole(principal.role)} replace />;
  }
  return <>{children}</>;
}
